<script>
import get from 'lodash/get'
import { mapActions, mapGetters, mapState } from 'vuex'
import ValidationMixin from '@lib/mixins/validation'

export default {
  name: 'CategoryForm',
  mixins: [ValidationMixin],
  props: {
    category: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      isEdit: this.$route.params.edit,
      form: {
        name: null,
        providerId: null,
      },
      rules: {
        providerId: {
          required: true,
          message: 'Provider is required',
        },
        name: [
          {
            required: true,
            message: 'This field is required',
          },
          {
            max: 40,
            message: 'Name must be 40 characters or less',
          },
        ],
      },
      backRoute: { name: 'categories' },
      progress: false,
      showDeleteDialog: false,
    }
  },
  computed: {
    ...mapGetters('me', [
      'canDeleteCategory',
      'canUpdateCategory',
      'canCreateCategory',
      'isSuperAdmin',
    ]),
    ...mapState('me', [
      'provider',
    ]),
    ...mapState('providers', [
      'providers',
    ]),
    providerName() {
      return get(this.provider, 'name', '')
    },
    meProviderId() {
      return get(this.provider, 'id')
    },
    isFormChanged() {
      return this.category.name !== this.form.name
    },
    isSaveBtnDisabled() {
      return (this.isEdit && !this.isFormChanged) || this.progress
    },
  },
  mounted() {
    if (this.isEdit) {
      this.form = { ...this.category }
    }
  },
  methods: {
    ...mapActions('category', ['createCategory', 'updateCategory', 'deleteCategory']),
    goBack() {
      this.$router.replace(this.backRoute)
    },
    onCancel() {
      this.$router.back()
    },
    transformFormData(data) {
      return {
        ...data,
        providerId: undefined,
      }
    },
    async onSubmit() {
      this.clearBackendErrors()
      if (this.isSaveBtnDisabled) return

      const formValid = await this.$refs.form.validate().catch(() => false)
      if (!formValid) return

      this.progress = true

      const options = { headers: { 'x-provider-id': this.form.providerId } }
      const response = await this[this.isEdit ? 'updateCategory' : 'createCategory']({
        form: this.transformFormData(this.form),
        options,
      })
      const [error] = response

      if (error) {
        this.processBackendErrors(error)
      }

      this.$notifier({
        response,
        errorTitle: `Unable to ${this.isEdit ? 'change' : 'add'} category`,
        successTitle: 'Category added',
        successMsg: `Category successfully ${this.isEdit ? 'changed' : 'added'}`,
      }).then(success => {
        if (success) {
          this.goBack()
        }
      })

      this.progress = false
    },
    async onDelete() {
      this.showDeleteDialog = false
      this.progress = true

      await this.deleteCategory(this.form.id).then(response => {
        const [error] = response

        this.$notifier({
          response,
          errorTitle: 'Unable to delete category',
          errorMsg: get(error, 'detail', undefined),
          successTitle: 'Category deleted',
          successMsg: 'Category successfully deleted',
        }).then(success => {
          if (success) {
            this.goBack()
          }
        })
      })

      this.progress = false
    },
  },
}
</script>

<template>
  <div>
    <el-form
      ref="form"
      :model="form"
      :rules="rules"
      label-position="top"
      class="card-form card-form--wide"
    >
      <el-form-item
        v-if="meProviderId"
        label="Provider Name"
      >
        <el-input
          disabled
          :value="providerName"
        />
      </el-form-item>
      <el-form-item
        v-else
        label="Provider Name"
        :prop="isSuperAdmin ? 'providerId': ''"
      >
        <el-select
          v-model="form.providerId"
          :disabled="!!category.providerId"
          placeholder="Select provider"
          :error="backendErrors.providerId"
        >
          <el-option
            v-for="provider in providers"
            :key="provider.id"
            :label="provider.name"
            :value="provider.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item
        label="Category Name"
        prop="name"
        :error="backendErrors.name"
      >
        <el-input v-model="form.name" />
      </el-form-item>

      <div class="card-form__footer">
        <div v-if="isEdit">
          <el-button
            v-if="canDeleteCategory"
            type="danger"
            :disabled="progress"
            @click.stop="showDeleteDialog = true"
          >
            Delete category
          </el-button>
        </div>
        <div>
          <el-button
            :disabled="progress"
            @click="onCancel"
          >
            Cancel
          </el-button>
          <el-button
            v-if="isEdit ? canUpdateCategory : canCreateCategory"
            type="primary"
            :disabled="isSaveBtnDisabled"
            @click="onSubmit"
          >
            Save category
          </el-button>
        </div>
      </div>
    </el-form>

    <state-dialog
      :visible.sync="showDeleteDialog"
      title="Delete Category"
      brand="danger"
    >
      <el-icon
        slot="icon"
        name="warning"
      />
      <el-button
        slot="subicon"
        icon="el-icon-delete"
        type="danger"
        circle
      />
      <div :class="$style.msg">
        Are you sure you want to delete this category?
      </div>
      <div class="modal__footer">
        <el-button
          type="danger"
          class="el-button--wide"
          @click="onDelete"
        >
          Delete category
        </el-button>
      </div>
    </state-dialog>
  </div>
</template>

<style lang="scss" module>
  .msg {
    text-align: center;
    white-space: nowrap;
  }

  .subicon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    font-size: 16px;
    color: var(--color-primary-text);
    background-color: var(--color-error);
    border-radius: 50%
  }
</style>
